import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import '@livekit/components-styles';
import MeetingRoom from '@/features/meeting-room/MeetingRoom';
import { livekitApi, type ConnectionDetails } from '@/features/meeting-room/livekit.api';
import { meetingApi } from '@/features/meetings/meeting.api';
import { extractError } from '@/lib/axios';

type Status = 'connecting' | 'waiting' | 'connected' | 'error' | 'ended';

export default function MeetingRoomPage(): JSX.Element {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const participantId = params.get('participant');

  const [status, setStatus] = useState<Status>('connecting');
  const [details, setDetails] = useState<ConnectionDetails | null>(null);
  const [title, setTitle] = useState('Meeting');
  const [error, setError] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const tryConnect = useCallback(async () => {
    if (!id || !participantId) {
      setStatus('error');
      setError('Missing meeting or participant. Join from the join page.');
      return;
    }
    try {
      const d = await livekitApi.getConnectionDetails(id, participantId);
      setDetails(d);
      setStatus('connected');
      if (pollRef.current) clearInterval(pollRef.current);
    } catch (err) {
      const e = extractError(err);
      if (e.code === 'FORBIDDEN') {
        setStatus('waiting'); // awaiting host admission — keep polling
      } else {
        setStatus('error');
        setError(e.message);
      }
    }
  }, [id, participantId]);

  useEffect(() => {
    meetingApi
      .get(id ?? '')
      .then((m) => setTitle(m.title))
      .catch(() => undefined);
    void tryConnect();
    pollRef.current = setInterval(() => {
      setStatus((s) => {
        if (s === 'waiting') void tryConnect();
        return s;
      });
    }, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [id, tryConnect]);

  const onLeave = useCallback(() => navigate('/dashboard'), [navigate]);

  if (status === 'connected' && details && id) {
    return <MeetingRoom meetingId={id} title={title} details={details} onLeave={onLeave} />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 text-slate-200">
      {status === 'error' ? (
        <>
          <p className="text-lg font-medium text-red-400">Unable to join</p>
          <p className="text-sm text-slate-500">{error}</p>
          <button className="btn-primary mt-2" onClick={() => navigate('/join')}>
            Back to join
          </button>
        </>
      ) : (
        <>
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          <p className="text-lg font-medium">
            {status === 'waiting' ? 'Waiting for the host to admit you…' : 'Connecting…'}
          </p>
        </>
      )}
    </div>
  );
}
